import { useState } from 'react';
import { MessageSquarePlus, Send } from 'lucide-react';
import { useLocation } from 'react-router';
import { AppSheet } from '@/components/ui/app-sheet';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useFeature } from '@/hooks/useCapabilities';
import { useSubmitUserReport, type ReportKind } from '@/hooks/useSubmitUserReport';
import { config } from '@/config';

const KIND_OPTIONS: { value: ReportKind; label: string }[] = [
  { value: 'bug', label: 'Bug' },
  { value: 'feature', label: 'Feature request' },
  { value: 'other', label: 'Other' },
];

/**
 * ReportIssueButton — the TopBar affordance for filing a bug report or feature
 * request. Icon button in the trailing cluster; opens an AppSheet with a
 * two-field form. Page and account context are attached automatically (the
 * session identifies the reporter server-side), so the user only describes the
 * problem.
 *
 * SaaS-only: reports land in the operator console, which the trial edition
 * doesn't ship, so the button hides itself when the edition lacks the feature.
 */
export function ReportIssueButton() {
  const enabled = useFeature('userReports');
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState<ReportKind>('bug');
  const [description, setDescription] = useState('');
  const submit = useSubmitUserReport();

  if (!enabled) return null;

  const close = () => {
    setOpen(false);
    setKind('bug');
    setDescription('');
  };

  const handleSubmit = () => {
    if (!description.trim() || submit.isPending) return;
    submit.mutate(
      {
        kind,
        description: description.trim(),
        pageUrl: location.pathname + location.search,
        appVersion: config.appEnvLabel || undefined,
      },
      { onSuccess: close },
    );
  };

  return (
    <>
      <button
        type="button"
        onClick={() => { setOpen(true); }}
        className="inline-flex h-11 w-11 md:h-10 md:w-10 items-center justify-center rounded-md text-foreground hover:bg-accent"
        aria-label="Report a problem or request a feature"
        title="Report a problem or request a feature"
      >
        <MessageSquarePlus className="w-5 h-5 md:w-4 md:h-4" />
      </button>

      <AppSheet
        open={open}
        onOpenChange={(o) => {
          if (!o) close();
        }}
        title="Report an issue"
        description="Spotted a bug or missing something you need? It goes straight to the team."
      >
        <AppSheet.Body>
          <form
            id="user-report-form"
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit();
            }}
            className="space-y-4"
          >
            <div className="space-y-1.5">
              <Label htmlFor="report-kind">Type</Label>
              <Select value={kind} onValueChange={(v) => { setKind(v as ReportKind); }}>
                <SelectTrigger id="report-kind">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {KIND_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="report-description">
                {kind === 'feature' ? 'What do you need?' : 'What happened?'}
              </Label>
              <Textarea
                id="report-description"
                rows={5}
                maxLength={5000}
                value={description}
                onChange={(e) => { setDescription(e.target.value); }}
                placeholder={
                  kind === 'feature'
                    ? 'Describe the feature and what it would help you do'
                    : 'What went wrong, and what did you expect to happen?'
                }
              />
            </div>

            <p className="text-xs text-muted-foreground">
              Your current page and account are included automatically so we can follow up.
            </p>
          </form>
        </AppSheet.Body>
        <AppSheet.Footer>
          <Button variant="outline" onClick={close} disabled={submit.isPending}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="user-report-form"
            disabled={submit.isPending || !description.trim()}
          >
            <Send className="w-4 h-4" />
            {submit.isPending ? 'Sending…' : 'Send report'}
          </Button>
        </AppSheet.Footer>
      </AppSheet>
    </>
  );
}
